import { useState } from 'react'
import { Label } from '@teach/ui'
import type { CourseWithoutBlocks, CourseUpdate } from '@/types/course'
import { PRICE_RUB_MIN, PRICE_USD_MIN, toNumber } from '../../lib/coursePatch'
import { readSalesModel, resolveSalesModel, salesModelPatch } from '../../lib/salesModel'
import type { SalesModel, SalesModelOverride } from '../../lib/salesModel'
import { PriceInputs } from './PriceInputs'
import { TrialAccessRow } from './TrialAccessRow'
import { SalesModelPicker } from './SalesModelPicker'
import { useCourseModeration } from '@/features/moderation/useCourseModeration'
import { SalesModelInfoPanel } from './SalesModelInfoPanel'
import { FaqList } from '@/components/common/FaqList'
import { STANDALONE_FAQ, SUBSCRIPTION_FAQ } from '../../lib/monetizationFaq'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
  local: CourseWithoutBlocks
  setLocal: (next: CourseWithoutBlocks) => void
  save: (patch: CourseUpdate) => void
  isCourseAuthor: boolean
}

export const PricingSection = ({ course, local, setLocal, save, isCourseAuthor }: Props) => {
  // Режим ВЫВОДИТСЯ из курса на каждом рендере, а в состоянии живёт только выбор
  // пользователя поверх него. Считать из `local` нельзя: обнуление обеих цен во время
  // печати в standalone схлопывало бы режим в free и поля исчезали бы из-под курсора —
  // поэтому источник именно `course`, он меняется только после сохранения.
  // Одного useState(() => readSalesModel(course)) мало: он сеется на маунте таба, а
  // course после PUT приходит с задержкой (updateCourse только инвалидирует тег), и
  // вернувшийся в таб автор до конца сессии видел бы устаревший режим.
  // Оверрайд живёт ровно до тех пор, пока курс ему не противоречит (см. resolveSalesModel).
  const m = useMessages(messages)
  const derived = readSalesModel(course)
  const [override, setOverride] = useState<SalesModelOverride | null>(null)
  const model = resolveSalesModel(derived, override)
  // Ноль легитимен у бесплатного (обе цены 0 — их и не показываем, PriceInputs здесь
  // не рендерится) и у подписки (курс может не продаваться отдельно, цены штатно
  // нулевые). Запрещён он только у standalone — там курс продаётся отдельно и цена
  // обязана быть. `model === 'free'` был бы неверен вдвойне: подписка с нулевыми
  // ценами получала бы zeroAllowed=false и встречала автора ложной ошибкой порога, а
  // сам проп внутри ветки ниже (`model !== 'free'`) был бы всегда false — мёртвый код.
  const zeroAllowed = model !== 'standalone'

  const changeModel = (next: SalesModel) => {
    // «Отдельно» с нулевой ценой бессмысленно: сразу ставим минимум платёжных систем
    // (выше уже набранной цены ничего не трогаем).
    const patch =
      next === 'standalone'
        ? {
            ...salesModelPatch(next),
            priceRub: Math.max(PRICE_RUB_MIN, toNumber(local.priceRub)),
            usdPrice: Math.max(PRICE_USD_MIN, toNumber(local.usdPrice)),
          }
        : salesModelPatch(next)
    setOverride({ from: derived, picked: next })
    setLocal({ ...local, ...patch })
    save(patch)
  }

  // Права автора — из состояния модерации курса (`canPublishToCatalog`).
  const { state: moderation } = useCourseModeration(course.id)
  return (
    <div className="space-y-6 [&_label]:text-lg [&_label]:font-medium">
      <SalesModelPicker
        value={model}
        onChange={changeModel}
        disabled={!isCourseAuthor}
        freeOnly={moderation?.canPublishToCatalog === false}
      />

      {model !== 'standalone' ? <SalesModelInfoPanel model={model} /> : null}

      {model !== 'free' ? (
        <>
          {/* Разовая покупка навсегда — только у «Отдельно»; в подписке цены нет. */}
          {model === 'standalone' ? (
            <PriceInputs
              local={local}
              setLocal={setLocal}
              save={save}
              isCourseAuthor={isCourseAuthor}
              zeroAllowed={zeroAllowed}
            />
          ) : null}

          {/* Поля «Базовая цена» здесь нет (п.11 правок 29 августа): в прототипе
              такой функции нет, а скидка станет отдельным окном с процентом,
              который считается от цены выше. Поле `basePrice` контракта при этом
              остаётся — студия его просто не правит. */}

          <TrialAccessRow
            local={local}
            setLocal={setLocal}
            save={save}
            isCourseAuthor={isCourseAuthor}
          />
        </>
      ) : null}

      {/* Вопросы-ответы — у подписки и у «Отдельно»; у бесплатного курса отвечать нечего. */}
      {model !== 'free' ? (
        <div className="space-y-3">
          <Label>{m.faq}</Label>
          <FaqList items={model === 'subscription' ? SUBSCRIPTION_FAQ : STANDALONE_FAQ} />
        </div>
      ) : null}
    </div>
  )
}
